/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Modal, Button, Form, Input } from 'tea-component';
import { useLocal } from '@/pages/dashboard/utils/customHook';
import { debounce } from 'lodash-es';
import { getSaveValue, setSaveValue } from './util';
import { DATE_FILTER_ARR } from './_config';
import classnames from 'classnames';
import DynamicFilterControl from '@gp/components/DynamicFilter/components/FilterControl';
import useFlexSpacingLayoutInfo, {
  FLEX_ITEM_HORIZONTAL_SPACING,
  FLEX_ITEM_VERTICAL_SPACING,
} from '@gp/hooks/useFlexSpacingLayoutInfo';
import RangePickerButton from '@gp/components/RangePickerButton';

export default function SaveFilterSetting(props: any) {
  const {
    visible,
    close,
    filterValue: defaultValue,
    filterSaveList,
    type,
    editFilter,
    addFilterSaveListFetch,
    editFilterSaveFetch,
    currentPage,
    fields,
    controls,
    isNeedDiff,
    onFieldFocus,
    onFieldBlur,
  } = props;

  const root = useRef<HTMLDivElement | null>(null);
  const { formatMessage } = useLocal('filter');
  const [errorMessage, setErrorMessage] = useState(''); // 错误校验信息
  const [filterName, setFilterName] = useState('');
  const [formValue, setFormValue] = useState(defaultValue);
  const [isErrorStauts, setIsErrorStauts] = useState(false);
  const filterItemsLayoutInfos = useFlexSpacingLayoutInfo(root, fields);

  // 方案名称列表
  const filterNameList = useMemo(
    () => filterSaveList?.map((item: any) => item?.name?.trim() || ''),
    [filterSaveList],
  );

  const { dateFilterArr, otherFilterArr } = useMemo(() => {
    // 日期筛选器
    const dateFilterArr =
      fields?.filter((item: any) => {
        const key = item?.type || item?.key;
        return DATE_FILTER_ARR?.includes(key);
      }) || [];

    // 其他筛选器
    const otherFilterArr =
      fields?.filter((item: any) => {
        const key = item?.type || item?.key;
        return !DATE_FILTER_ARR?.includes(key);
      }) || [];

    return {
      dateFilterArr,
      otherFilterArr,
    };
  }, [formValue, fields]);

  const setErrorFunc = (val: string) => {
    const isHasSameName = filterNameList?.includes(val?.trim());
    const isSameNameError =
      type === 'edit' ? isHasSameName && val?.trim() !== editFilter?.name?.trim() : isHasSameName;
    const isNull = val?.trim() === '';
    const isOverNumError = val?.trim()?.length > 30;
    if (isNull) {
      setIsErrorStauts(true);
      setErrorMessage('Please input Name');
      return;
    }

    setIsErrorStauts(isSameNameError || isOverNumError);
    const message = isSameNameError
      ? 'A setting with the same name already exists, click confirm to replace this setting.'
      : isOverNumError
      ? 'A maximum of 30 characters are supported.'
      : '';
    setErrorMessage(isSameNameError || isOverNumError ? message : '');
  };

  // 编辑弹窗默认赋值逻辑
  useEffect(() => {
    if (type === 'edit') {
      const { name, filter_value: filterValueTemp } = editFilter || {};
      setErrorFunc(name);
      setFilterName(name || '');
      // 编辑需要格式化数据（时间和筛选器变化）
      const newValue = getSaveValue(defaultValue, filterValueTemp, fields);
      setFormValue(newValue);
      // setIsDefault(is_default);
    } else {
      setFormValue(defaultValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, editFilter, fields, defaultValue]);

  const handleNameOnChange = (val: string) => {
    setFilterName(val);
    setErrorFunc(val);
  };

  const handleOnSubmit = () => {
    setErrorFunc(filterName);
    const isNull = filterName?.trim() === '';
    if (isNull || (errorMessage && type === 'add')) {
      return;
    }

    const params: any = {
      name: filterName,
      page: currentPage,
      filter_value: formValue,
    };

    if (type === 'edit') {
      const id = filterNameList?.includes(filterName)
        ? filterSaveList?.find((item: any) => item?.name === filterName)?.id
        : editFilter?.id;

      const editParams = {
        ...params,
        id,
      };

      editFilterSaveFetch(setSaveValue(editParams, fields));
    } else {
      addFilterSaveListFetch(setSaveValue(params, fields));
    }
    close();
  };

  const onFilterChange = (key: string, value: any) => {
    const newValue = {
      ...formValue,
      [key]: value,
    };
    setFormValue(newValue);
  };

  return (
    <Modal
      visible={visible}
      size={800}
      className="dbg-save-filter-setting"
      destroyOnClose
      caption={type === 'edit' ? 'Edit Filter Setting' : 'Save as My Filter'}
      onClose={close}
    >
      <Modal.Body>
        <div className="dbg-save-filter-setting-des">
          <p>
            {
              'Saved settings can be used for the same filter criteria in Pipeline. Note: Time filters are unique to each page and do not carry over.'
            }
          </p>
        </div>
        <Form className="setting-table">
          <Form.Item
            label={<div className="dbg-modal-form-lable">{'Setting Name'}</div>}
            showStatusIcon={false}
            className={classnames({
              'dbg-modal-form-item-error-stats': !!isErrorStauts,
            })}
            status={
              // eslint-disable-next-line no-nested-ternary
              errorMessage
                ? type === 'edit' && filterNameList?.includes(filterName?.trim())
                  ? 'validating'
                  : 'error'
                : 'success'
            }
            message={errorMessage}
          >
            <Input
              placeholder={'Please input Name'}
              size="full"
              value={filterName}
              onChange={handleNameOnChange}
            />
          </Form.Item>
          {dateFilterArr?.length > 0 && (
            <Form.Item
              label={<div className="dbg-filter-lable">{'Time Settings'}</div>}
              showStatusIcon={false}
            >
              {dateFilterArr
                ?.filter((field: any) => field?.display ?? true)
                ?.map((field: any, fieldIndex: any) => {
                  const {
                    style: fieldStyle,
                    className: fieldClassName,
                    key,
                    type,
                    label,
                    saveKey,
                  } = field || {};
                  const control = { ...controls?.[key], isNeedDiff } as any;
                  const {
                    marginRight = FLEX_ITEM_HORIZONTAL_SPACING,
                    marginBottom = FLEX_ITEM_VERTICAL_SPACING,
                  } = filterItemsLayoutInfos[fieldIndex] || {};

                  const labelCur =
                    key === 'dueDate' ? (
                      <RangePickerButton.CommonDateRangePickerButton
                        name="DUE"
                        value={formValue?.[key]}
                      />
                    ) : saveKey === 'checklist_rangePicker' ? (
                      <RangePickerButton.CommonDateRangePickerButton value={formValue?.[key]} />
                    ) : (
                      label
                    );

                  return (
                    <div
                      key={key}
                      style={{ ...fieldStyle, marginRight, marginBottom }}
                      className={classnames('dbg-dynamic-filter__filter-item', fieldClassName, {
                        'is-disabled': !!control?.disabled,
                      })}
                    >
                      <DynamicFilterControl
                        type={type}
                        label={labelCur}
                        control={control}
                        value={formValue?.[key]}
                        onChange={(payload: any) => {
                          onFilterChange(key, payload);
                        }}
                        onFocus={onFieldFocus}
                        onBlur={onFieldBlur}
                      />
                    </div>
                  );
                })}
            </Form.Item>
          )}
          {otherFilterArr?.length > 0 && (
            <Form.Item
              label={<div className="dbg-filter-location">{'Other Settings'}</div>}
              showStatusIcon={false}
            >
              <div className="setting-group mod-filter">
                {otherFilterArr
                  ?.filter((field: any) => field?.display ?? true)
                  ?.map((field: any, fieldIndex: any) => {
                    const {
                      style: fieldStyle,
                      className: fieldClassName,
                      key,
                      type,
                      label,
                    } = field || {};
                    const control = { ...controls?.[key], isNeedDiff } as any;

                    const {
                      marginRight = FLEX_ITEM_HORIZONTAL_SPACING,
                      marginBottom = FLEX_ITEM_VERTICAL_SPACING,
                    } = filterItemsLayoutInfos[fieldIndex] || {};

                    return (
                      <div
                        key={key}
                        style={{ ...fieldStyle, marginRight, marginBottom }}
                        className={classnames('dbg-dynamic-filter__filter-item', fieldClassName, {
                          'is-disabled': !!control?.disabled,
                        })}
                      >
                        <DynamicFilterControl
                          type={type}
                          label={label}
                          control={control}
                          value={formValue?.[key]}
                          onChange={(payload: any) => {
                            onFilterChange(key, payload);
                          }}
                          onFocus={onFieldFocus}
                          onBlur={onFieldBlur}
                        />
                      </div>
                    );
                  })}
              </div>
            </Form.Item>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button type="primary" onClick={debounce(handleOnSubmit, 300)}>
          {formatMessage({ id: 'common_confirm' })}
        </Button>
        <Button type="weak" onClick={close}>
          {formatMessage({ id: 'common_cancel' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
